import { getNonDeletedElements } from "@excalidraw/element";

import { updateFrameMembershipOfSelectedElements } from "@excalidraw/element";

import { KEYS, arrayToMap, getShortcutKey } from "@excalidraw/common";

import {
  alignElements,
  alignElementToFrame,
  alignGroupToFrame,
  reapplyAutoLayoutToAllFrames,
} from "@excalidraw/element";
import { getMaximumGroups } from "@excalidraw/element";

import { CaptureUpdateAction } from "@excalidraw/element";

import type { ElementsMap } from "@excalidraw/element/types";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import type { Alignment } from "@excalidraw/element";

import { ToolButton } from "../components/ToolButton";
import {
  AlignBottomIcon,
  AlignLeftIcon,
  AlignRightIcon,
  AlignTopIcon,
  CenterHorizontallyIcon,
  CenterVerticallyIcon,
} from "../components/icons";

import { t } from "../i18n";

import { isSomeElementSelected } from "../scene";

import { register } from "./register";

import type { AppClassProperties, AppState, UIAppState } from "../types";

export const alignActionsPredicate = (
  appState: UIAppState,
  app: AppClassProperties,
) => {
  const selectedElements = app.scene.getSelectedElements(appState);

  // Allow alignment for multiple elements (including frames)
  if (selectedElements.length > 1) {
    return true;
  }

  // Allow alignment for single element/group inside a frame (existing behavior)
  if (selectedElements.length === 1) {
    const element = selectedElements[0];
    return Boolean(element.frameId);
  }

  return false;
};

/**
 * Helper function to check if all elements are in the same frame
 */
const areAllElementsInSameFrame = (
  selectedElements: ExcalidrawElement[],
): string | null => {
  if (selectedElements.length === 0) {
    return null;
  }

  const firstFrameId = selectedElements[0].frameId;
  if (!firstFrameId) {
    return null;
  }

  const allInSameFrame = selectedElements.every(
    (el) => el.frameId === firstFrameId,
  );
  return allInSameFrame ? firstFrameId : null;
};

/**
 * Helper function to check if selected elements form a single group
 */
const isSelectionSingleGroup = (
  selectedElements: ExcalidrawElement[],
  elementsMap: ElementsMap,
): boolean => {
  if (selectedElements.length <= 1) {
    return true;
  }

  const groups = getMaximumGroups(selectedElements, elementsMap);
  return groups.length === 1;
};

const alignSelectedElements = (
  elements: readonly ExcalidrawElement[],
  appState: Readonly<AppState>,
  app: AppClassProperties,
  alignment: Alignment,
) => {
  const selectedElements = app.scene.getSelectedElements(appState);
  const elementsMap = app.scene.getNonDeletedElementsMap();

  // Check if all elements are in the same frame
  const commonFrameId = areAllElementsInSameFrame(selectedElements);

  // Handle single element/group in frame → align to frame
  if (
    commonFrameId &&
    (selectedElements.length === 1 ||
      isSelectionSingleGroup(selectedElements, elementsMap))
  ) {
    const frame = elementsMap.get(commonFrameId);

    if (frame) {
      if (selectedElements.length === 1) {
        // Single element case
        const updatedElements = alignElementToFrame(
          selectedElements[0],
          frame,
          alignment,
          app.scene,
        );
        const updatedElementsMap = arrayToMap(updatedElements);

        return elements.map((el) => updatedElementsMap.get(el.id) || el);
      }
      // Single group case - align entire group to frame
      const updatedElements = alignGroupToFrame(
        selectedElements,
        frame,
        alignment,
        app.scene,
      );
      const updatedElementsMap = arrayToMap(updatedElements);

      return elements.map((el) => updatedElementsMap.get(el.id) || el);
    }
  }

  // Handle multiple elements/groups → align to each other (existing behavior)
  const updatedElements = alignElements(selectedElements, alignment, app.scene);
  const updatedElementsMap = arrayToMap(updatedElements);

  return updateFrameMembershipOfSelectedElements(
    elements.map((element) => updatedElementsMap.get(element.id) || element),
    appState,
    app,
  );
};

// Helper function to trigger auto layout rerendering after alignment actions
// This ensures that child elements within frames are properly repositioned
const triggerAutoLayoutRerendering = (app: AppClassProperties) => {
  // Apply the same layered rerendering pattern used to fix previous auto layout rerender issues
  setTimeout(() => {
    const allElements = app.scene.getElementsIncludingDeleted();
    const elementsMap = app.scene.getNonDeletedElementsMap();
    const updatedElements = reapplyAutoLayoutToAllFrames(
      allElements,
      elementsMap,
    );
    if (updatedElements !== allElements) {
      app.scene.replaceAllElements(updatedElements);

      // Trigger additional auto layout rerendering for nested frames
      setTimeout(() => {
        const currentElements = app.scene.getElementsIncludingDeleted();
        const currentElementsMap = app.scene.getNonDeletedElementsMap();
        const rerenderedElements = reapplyAutoLayoutToAllFrames(
          currentElements,
          currentElementsMap,
        );
        if (rerenderedElements !== currentElements) {
          app.scene.replaceAllElements(rerenderedElements);
        }

        // Final trigger to ensure visual re-rendering
        app.scene.triggerUpdate();
      }, 0);
    } else {
      // Even if no layout changes, trigger update for visual feedback
      app.scene.triggerUpdate();
    }
  }, 0);
};

export const actionAlignTop = register({
  name: "alignTop",
  label: "labels.alignTop",
  icon: AlignTopIcon,
  trackEvent: { category: "element" },
  predicate: (elements, appState, appProps, app) =>
    alignActionsPredicate(appState, app),
  perform: (elements, appState, _, app) => {
    // Trigger auto layout rerendering to ensure child elements within frames are repositioned
    triggerAutoLayoutRerendering(app);

    return {
      appState,
      elements: alignSelectedElements(elements, appState, app, {
        position: "start",
        axis: "y",
      }),
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
  keyTest: (event) =>
    event[KEYS.CTRL_OR_CMD] && event.shiftKey && event.key === KEYS.ARROW_UP,
  PanelComponent: ({ elements, appState, updateData, app }) => (
    <ToolButton
      hidden={!alignActionsPredicate(appState, app)}
      type="button"
      icon={AlignTopIcon}
      onClick={() => updateData(null)}
      title={`${t("labels.alignTop")} — ${getShortcutKey(
        "CtrlOrCmd+Shift+Up",
      )}`}
      aria-label={t("labels.alignTop")}
      visible={isSomeElementSelected(getNonDeletedElements(elements), appState)}
    />
  ),
});

export const actionAlignBottom = register({
  name: "alignBottom",
  label: "labels.alignBottom",
  icon: AlignBottomIcon,
  trackEvent: { category: "element" },
  predicate: (elements, appState, appProps, app) =>
    alignActionsPredicate(appState, app),
  perform: (elements, appState, _, app) => {
    // Trigger auto layout rerendering to ensure child elements within frames are repositioned
    triggerAutoLayoutRerendering(app);

    return {
      appState,
      elements: alignSelectedElements(elements, appState, app, {
        position: "end",
        axis: "y",
      }),
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
  keyTest: (event) =>
    event[KEYS.CTRL_OR_CMD] && event.shiftKey && event.key === KEYS.ARROW_DOWN,
  PanelComponent: ({ elements, appState, updateData, app }) => (
    <ToolButton
      hidden={!alignActionsPredicate(appState, app)}
      type="button"
      icon={AlignBottomIcon}
      onClick={() => updateData(null)}
      title={`${t("labels.alignBottom")} — ${getShortcutKey(
        "CtrlOrCmd+Shift+Down",
      )}`}
      aria-label={t("labels.alignBottom")}
      visible={isSomeElementSelected(getNonDeletedElements(elements), appState)}
    />
  ),
});

export const actionAlignLeft = register({
  name: "alignLeft",
  label: "labels.alignLeft",
  icon: AlignLeftIcon,
  trackEvent: { category: "element" },
  predicate: (elements, appState, appProps, app) =>
    alignActionsPredicate(appState, app),
  perform: (elements, appState, _, app) => {
    // Trigger auto layout rerendering to ensure child elements within frames are repositioned
    triggerAutoLayoutRerendering(app);

    return {
      appState,
      elements: alignSelectedElements(elements, appState, app, {
        position: "start",
        axis: "x",
      }),
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
  keyTest: (event) =>
    event[KEYS.CTRL_OR_CMD] && event.shiftKey && event.key === KEYS.ARROW_LEFT,
  PanelComponent: ({ elements, appState, updateData, app }) => (
    <ToolButton
      hidden={!alignActionsPredicate(appState, app)}
      type="button"
      icon={AlignLeftIcon}
      onClick={() => updateData(null)}
      title={`${t("labels.alignLeft")} — ${getShortcutKey(
        "CtrlOrCmd+Shift+Left",
      )}`}
      aria-label={t("labels.alignLeft")}
      visible={isSomeElementSelected(getNonDeletedElements(elements), appState)}
    />
  ),
});

export const actionAlignRight = register({
  name: "alignRight",
  label: "labels.alignRight",
  icon: AlignRightIcon,
  trackEvent: { category: "element" },
  predicate: (elements, appState, appProps, app) =>
    alignActionsPredicate(appState, app),
  perform: (elements, appState, _, app) => {
    // Trigger auto layout rerendering to ensure child elements within frames are repositioned
    triggerAutoLayoutRerendering(app);

    return {
      appState,
      elements: alignSelectedElements(elements, appState, app, {
        position: "end",
        axis: "x",
      }),
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
  keyTest: (event) =>
    event[KEYS.CTRL_OR_CMD] && event.shiftKey && event.key === KEYS.ARROW_RIGHT,
  PanelComponent: ({ elements, appState, updateData, app }) => (
    <ToolButton
      hidden={!alignActionsPredicate(appState, app)}
      type="button"
      icon={AlignRightIcon}
      onClick={() => updateData(null)}
      title={`${t("labels.alignRight")} — ${getShortcutKey(
        "CtrlOrCmd+Shift+Right",
      )}`}
      aria-label={t("labels.alignRight")}
      visible={isSomeElementSelected(getNonDeletedElements(elements), appState)}
    />
  ),
});

export const actionAlignVerticallyCentered = register({
  name: "alignVerticallyCentered",
  label: "labels.centerVertically",
  icon: CenterVerticallyIcon,
  trackEvent: { category: "element" },
  predicate: (elements, appState, appProps, app) =>
    alignActionsPredicate(appState, app),
  perform: (elements, appState, _, app) => {
    // Trigger auto layout rerendering to ensure child elements within frames are repositioned
    triggerAutoLayoutRerendering(app);

    return {
      appState,
      elements: alignSelectedElements(elements, appState, app, {
        position: "center",
        axis: "y",
      }),
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
  PanelComponent: ({ elements, appState, updateData, app }) => (
    <ToolButton
      hidden={!alignActionsPredicate(appState, app)}
      type="button"
      icon={CenterVerticallyIcon}
      onClick={() => updateData(null)}
      title={t("labels.centerVertically")}
      aria-label={t("labels.centerVertically")}
      visible={isSomeElementSelected(getNonDeletedElements(elements), appState)}
    />
  ),
});

export const actionAlignHorizontallyCentered = register({
  name: "alignHorizontallyCentered",
  label: "labels.centerHorizontally",
  icon: CenterHorizontallyIcon,
  trackEvent: { category: "element" },
  predicate: (elements, appState, appProps, app) =>
    alignActionsPredicate(appState, app),
  perform: (elements, appState, _, app) => {
    // Trigger auto layout rerendering to ensure child elements within frames are repositioned
    triggerAutoLayoutRerendering(app);

    return {
      appState,
      elements: alignSelectedElements(elements, appState, app, {
        position: "center",
        axis: "x",
      }),
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
  PanelComponent: ({ elements, appState, updateData, app }) => (
    <ToolButton
      hidden={!alignActionsPredicate(appState, app)}
      type="button"
      icon={CenterHorizontallyIcon}
      onClick={() => updateData(null)}
      title={t("labels.centerHorizontally")}
      aria-label={t("labels.centerHorizontally")}
      visible={isSomeElementSelected(getNonDeletedElements(elements), appState)}
    />
  ),
});
